// src/components/DashboardAdmin/ICTData/ictCatalog/RelatedIctDomain.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import apiClient from "../../../../../store/apiClient";
import {
  deleteRelatedDomain,
  patchRelatedDomainStatus,
  updateRelatedDomain,
  insertRelatedDomain,
  fetchRelatedDomainsByIctId,
} from "../../../../../store/ictRelatedDomainSlice";
import { fetchDomains } from "../../../../../store/domainsSlice";

const RelatedIctDomain = ({ ictId, onClose, showInsertForm = false }) => {
  const dispatch = useDispatch();
  const { relatedDomains = [], loading, error } = useSelector(
    (state) => state.ictRelatedDomain || {}
  );
  const { domains = [] } = useSelector((state) => state.domains || {});
  const [editId, setEditId] = useState(null);
  const [editDomainId, setEditDomainId] = useState("");

  // Insert: user types domain id, we fetch /domains/{id} and show it as suggestion
  const [domainSearch, setDomainSearch] = useState(""); // visible text
  const [selectedDomainId, setSelectedDomainId] = useState("");
  const [domainSuggestions, setDomainSuggestions] = useState([]);

  // Cache domain details so we don't hit API repeatedly
  const [domainCache, setDomainCache] = useState({}); // { [id]: {id, domain, ...} }

  // Fetch related domains for this ict
  useEffect(() => {
    if (ictId && !showInsertForm) {
      dispatch(fetchRelatedDomainsByIctId(ictId));
    }
  }, [dispatch, ictId, showInsertForm]);
    useEffect(() => {
    dispatch(fetchDomains());
    }, [dispatch]);
    
  // Helper: fetch domain by id and cache it
  const fetchDomainById = async (id) => {
    const domainId = Number(id);
    if (!domainId || Number.isNaN(domainId)) return null;

    if (domainCache[domainId]) return domainCache[domainId];

    try {
      const res = await apiClient.get(`/domains/${domainId}`);
      const dom = res.data;
      setDomainCache((prev) => ({ ...prev, [domainId]: dom }));
      return dom;
    } catch {
      return null;
    }
  };

  // When view popup opens, prefetch domain names for existing records (best effort)
  useEffect(() => {
    if (showInsertForm) return;
    const ids = new Set();
    relatedDomains.forEach((r) => {
      const did = r.domainId ?? r.ictDomainId ?? r.relatedDomainId;
      if (did) ids.add(Number(did));
    });
    ids.forEach((id) => {
      if (!domainCache[id]) fetchDomainById(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedDomains, showInsertForm]);

  const getDomainName = (id) => {
    const dom = domainCache[Number(id)];
    return dom?.domain || dom?.domainName || ""; // fallback if backend uses different key
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this related Domain?")) {
      dispatch(deleteRelatedDomain(id));
    }
  };

  const handleStatusToggle = (item) => {
    const newStatus = item.status === "A" ? "D" : "A";
    dispatch(patchRelatedDomainStatus({ id: item.id, status: newStatus }));
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    const did = item.domainId ?? item.ictDomainId ?? item.relatedDomainId;
    setEditDomainId(did ?? "");
  };

  const handleSave = (item) => {
    dispatch(
      updateRelatedDomain({
        id: item.id,
        ictId: item.ictId ?? Number(ictId),
        domainId: Number(editDomainId),
        status: item.status,
      })
    );
    setEditId(null);
    setEditDomainId("");
  };

  const handleInsert = () => {
    if (!selectedDomainId) {
      alert("Please select a valid Domain (type Domain ID and select it).");
      return;
    }

    dispatch(
      insertRelatedDomain({
        ictId: Number(ictId),
        domainId: Number(selectedDomainId),
        status: "A",
      })
    );

    setDomainSearch("");
    setSelectedDomainId("");
    setDomainSuggestions([]);
    onClose();
  };

  // Live “suggestion” based on Domain ID -> /domains/{id}
  const handleDomainSearchChange = async (e) => {
    const value = e.target.value;
    setDomainSearch(value);
    setSelectedDomainId("");
    setDomainSuggestions([]);

    const trimmed = value.trim();
    if (!trimmed) return;

    // Expecting numeric id because we only have /domains/{domain_id}
    if (!/^\d+$/.test(trimmed)) return;

    const dom = await fetchDomainById(trimmed);
    if (dom?.id) {
      setDomainSuggestions([dom]);
    } else {
      setDomainSuggestions([]);
    }
  };

  const handleSelectDomain = (dom) => {
    setSelectedDomainId(dom.id);
    // Show domain name in input (still keeps id in selectedDomainId)
    setDomainSearch(dom.domain || dom.domainName || String(dom.id));
    setDomainSuggestions([]);
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content large">
        <div className="popup-header">
          <h3>
            {showInsertForm
              ? "Add Related Domain"
              : `Related Domains for ICT ID: ${ictId}`}
            <button className="close-btn" onClick={onClose}>
              <i className="fa fa-times"></i>
            </button>
          </h3>
        </div>

        {loading && !showInsertForm && <p>Loading related Domains...</p>}
        {error && !showInsertForm && (
          <p className="error">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </p>
        )}

        {showInsertForm ? (
          <div>
            <label>Domain:</label>
            <input
            type="text"
            value={domainSearch}
            onChange={(e) => {
                const value = e.target.value;
                setDomainSearch(value);

                if (!value.trim()) {
                setDomainSuggestions([]);
                setSelectedDomainId("");
                return;
                }

                const matches = domains.filter((d) =>
                d.domain.toLowerCase().includes(value.toLowerCase())
                );

                setDomainSuggestions(matches);
            }}
            placeholder="Type domain name..."
            className="input"
            />
            {domainSuggestions.length > 0 && (
            <div className="suggestions-dropdown">
                {domainSuggestions.map((dom) => (
                <div
                    key={dom.id}
                    className="suggestion-item"
                    onClick={() => {
                    setSelectedDomainId(dom.id);     // ✅ ID saved
                    setDomainSearch(dom.domain);     // ✅ Name shown
                    setDomainSuggestions([]);
                    }}
                >
                    {dom.domain} (ID: {dom.id})
                </div>
                ))}
            </div>
            )}
            {selectedDomainId && (
            <p style={{ fontSize: "0.85em", marginTop: "0.5rem" }}>
                Selected: <b>{domainSearch} (ID: {selectedDomainId})</b>
            </p>
            )}
            <hr />
            <button onClick={handleInsert}>Insert</button>
          </div>
        ) : (
          <>
            {relatedDomains.length === 0 ? (
              <p>No related Domains found.</p>
            ) : (
              <table className="table styled-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Domain ID</th>
                    <th>Domain Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedDomains.map((item, index) => {
                    const domainId = item.domainId ?? item.ictDomainId ?? item.relatedDomainId;

                    return (
                      <tr key={item.id}>
                        <td>{index + 1}</td>

                        <td>
                          {editId === item.id ? (
                            <input
                              type="number"
                              value={editDomainId}
                              onChange={(e) => setEditDomainId(e.target.value)}
                            />
                          ) : (
                            domainId
                          )}
                        </td>

                        <td>
                          {domainId
                            ? (getDomainName(domainId) || "Loading...")
                            : "-"}
                        </td>

                        <td>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={item.status === "A"}
                              onChange={() => handleStatusToggle(item)}
                            />
                            <span className="slider round"></span>
                          </label>
                        </td>

                        <td>
                          {editId === item.id ? (
                            <i
                              className="fa fa-save action-icon saveIcon"
                              onClick={() => handleSave(item)}
                            ></i>
                          ) : (
                            <i
                              className="fa fa-edit action-icon editIcon"
                              onClick={() => handleEdit(item)}
                            ></i>
                          )}
                          <i
                            className="fa fa-trash action-icon deleteIcon"
                            onClick={() => handleDelete(item.id)}
                          ></i>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RelatedIctDomain;
